<template>
  <div>
  <v-dialog v-model="visible" max-width="500" persistent>
    <v-card>
      <v-card-title>
        <v-icon left color="primary">mdi-account-plus</v-icon>
        {{ tipoFactura === 'FACTURA_B' ? 'Crear Cliente (Consumidor Final)' : 'Crear Cliente (Responsable Inscripto)' }}
      </v-card-title>
      
      <v-card-text>
        <v-form ref="form" v-model="formValid">
          <v-select
            v-model="form.tipoDocumento"
            :items="tiposDocumento"
            label="Tipo de Documento *"
            :rules="[v => !!v || 'Requerido']"
            outlined
            dense
          />
          
          <v-row no-gutters>
            <v-col cols="6" class="pr-2">
              <v-text-field
                :value="form.numeroDocumento"
                @input="onNumeroDocumentoInput"
                :label="$t('cliente.labels.numeroDocumento')"
                :placeholder="numeroDocumentoPlaceholder"
                :rules="documentoRules"
                outlined
                dense
              />
            </v-col>
            <v-col cols="6" class="pl-2">
              <v-btn
                v-if="afipActivo && tipoFactura === 'FACTURA_A' && form.tipoDocumento === 'CUIT'"
                color="primary"
                @click="consultarDatosArca"
                :loading="consultandoArca"
                :disabled="!form.numeroDocumento"
                block
                height="40"
              >
                <v-icon left small>mdi-cloud-search</v-icon>
                Buscar en AFIP
              </v-btn>
            </v-col>
          </v-row>
          
          <v-text-field
            v-model="form.razonSocial"
            :label="tipoFactura === 'FACTURA_B' ? $t('cliente.labels.nombrePersona') : $t('cliente.labels.razonSocial')"
            :rules="[v => !!v || 'Requerido']"
            outlined
            dense
          />
          
          <v-select
            v-model="form.condicionIVA"
            :items="condicionesIVA"
            label="Condición IVA *"
            :rules="[v => !!v || 'Requerido']"
            outlined
            dense
            :disabled="condicionIVADisabled"
            :hint="hintCondicionIVA"
            persistent-hint
          />
          
          <v-text-field
            v-model="form.email"
            label="Email (opcional)"
            type="email"
            outlined
            dense
            hint="Se usará para enviar la factura por email"
          />
          
          <v-alert v-if="error" type="error" dense>
            {{ error }}
          </v-alert>
        </v-form>
        
        <snackbar v-model="snackbarMessage" :color="snackbarColor" />
      </v-card-text>
      
      <v-card-actions>
        <v-spacer />
        <v-btn text @click="cancelar">Cancelar</v-btn>
        <v-btn 
          color="primary" 
          @click="crear"
          :disabled="!formValid"
          :loading="creando"
        >
          <v-icon left>mdi-check</v-icon>
          Crear y Usar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-dialog v-model="docVentaReminderOpen" max-width="520" persistent>
    <v-card>
      <v-card-title class="text-h6">
        <v-icon left color="amber darken-2">mdi-file-document-alert-outline</v-icon>
        {{ $t('cliente.documentosVentaReminderTitle') }}
      </v-card-title>
      <v-card-text class="pt-2">
        {{ $t('cliente.documentosVentaReminderText') }}
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text @click="docVentaReminderOpen = false">{{ $t('common.cancel') }}</v-btn>
        <v-btn color="primary" @click="confirmarDocumentosVenta">{{ $t('common.confirm') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  </div>
</template>

<script>
import ClienteProvider from '../../../cliente/providers/ClienteProvider'
import CompanyProvider from '../../../base/providers/CompanyProvider'
import { Snackbar } from '@dracul/common-frontend'
import { applyCuitCuilInput, formatCuitCuilDisplay } from '../../../../utils/cuitCuilFormat'

export default {
  components: { Snackbar },
  props: {
    value: Boolean,
    tipoFactura: {
      type: String,
      default: 'FACTURA_A'
    }
  },
  
  data() {
    return {
      docVentaReminderOpen: false,
      formValid: false,
      creando: false,
      error: null,
      consultandoArca: false,
      afipActivo: false,
      snackbarMessage: null,
      snackbarColor: 'success',
      form: {
        tipoDocumento: this.tipoFactura === 'FACTURA_B' ? 'DNI' : 'CUIT',
        numeroDocumento: '',
        razonSocial: '',
        condicionIVA: this.tipoFactura === 'FACTURA_B' ? 'CONSUMIDOR_FINAL' : 'RESPONSABLE_INSCRIPTO',
        email: ''
      }
    }
  },
  
  computed: {
    visible: {
      get() { return this.value },
      set(val) { this.$emit('input', val) }
    },
    
    documentoRules() {
      return [
        (v) => {
          if (!v || !String(v).trim()) return true
          return this.validarDocumento(String(v).trim()) || 'Documento inválido'
        },
      ]
    },
    
    tiposDocumento() {
      return [
        { text: 'DNI', value: 'DNI' },
        { text: 'CUIL', value: 'CUIL' },
        { text: 'CUIT', value: 'CUIT' }
      ]
    },
    
    condicionesIVA() {
      if (this.tipoFactura === 'FACTURA_B') {
        return [
          { text: 'Consumidor Final', value: 'CONSUMIDOR_FINAL' }
        ]
      }
      return [
        { text: 'Responsable Inscripto', value: 'RESPONSABLE_INSCRIPTO' }
      ]
    },
    
    condicionIVADisabled() {
      return true // Siempre deshabilitado según el tipo de factura
    },
    
    hintCondicionIVA() {
      if (this.tipoFactura === 'FACTURA_B') {
        return 'Solo se pueden crear clientes Consumidores Finales para Factura B'
      }
      return 'Solo se pueden crear clientes Responsables Inscriptos para Factura A'
    },
    esDocumentoCuitCuil() {
      return this.form.tipoDocumento === 'CUIT' || this.form.tipoDocumento === 'CUIL'
    },
    numeroDocumentoPlaceholder() {
      return this.esDocumentoCuitCuil ? '30-71513211-3' : this.$t('cliente.labels.numeroDocumento')
    },
  },

  async created() {
    await this.cargarAfipActivo()
  },

  watch: {
    'form.tipoDocumento'(val) {
      if ((val === 'CUIT' || val === 'CUIL') && this.form.numeroDocumento) {
        this.form.numeroDocumento = formatCuitCuilDisplay(this.form.numeroDocumento)
      }
    },
  },
  
  methods: {
    async cargarAfipActivo() {
      try {
        const response = await CompanyProvider.companyAfipStatus()
        const st = response.data && response.data.companyAfipStatus
        this.afipActivo = !!(st && st.afipActivo)
      } catch (e) {
        this.afipActivo = false
      }
    },
    onNumeroDocumentoInput(val) {
      if (this.esDocumentoCuitCuil) {
        this.form.numeroDocumento = applyCuitCuilInput(val)
      } else {
        this.form.numeroDocumento = String(val || '').replace(/\D/g, '').slice(0, 8)
      }
    },
    validarDocumento(documento) {
      if (!documento) return true
      if (this.form.tipoDocumento === 'CUIT' || this.form.tipoDocumento === 'CUIL') {
        return /^\d{11}$/.test(documento.replace(/[-]/g, ''))
      }
      if (this.form.tipoDocumento === 'DNI') {
        return /^\d{7,8}$/.test(documento)
      }
      return true
    },
    
    async consultarDatosArca() {
      if (!this.form.numeroDocumento) {
        this.snackbarColor = 'error'
        this.snackbarMessage = 'Por favor ingrese el número de documento'
        return
      }

      this.consultandoArca = true

      try {
        const cuitConsulta = String(this.form.numeroDocumento || '').replace(/\D/g, '')
        const response = await ClienteProvider.consultarArca(cuitConsulta)
        
        // Verificar que la respuesta tenga la estructura esperada
        if (!response || !response.data || !response.data.clienteConsultarArca) {
          throw new Error('Respuesta inválida del servidor')
        }

        const data = response.data.clienteConsultarArca

        if (data.success && data.persona && data.persona.datosGenerales) {
          const { persona } = data
          const dg = persona.datosGenerales
          
          // Autocompletar Razón Social desde AFIP
          if (dg.razonSocial && !this.form.razonSocial) {
            this.form.razonSocial = dg.razonSocial
          }

          // Autocompletar email (si existe y el campo está vacío)
          if (dg.email && dg.email.length > 0 && !this.form.email) {
            const emailPrincipal = dg.email[0]
            if (emailPrincipal.direccion) {
              this.form.email = emailPrincipal.direccion
            }
          }

          // Intentar determinar condición IVA desde impuestos
          if (dg.impuesto && dg.impuesto.length > 0) {
            const tieneIVA = dg.impuesto.some(imp => 
              imp.descripcionImpuesto && 
              (imp.descripcionImpuesto.includes('IVA') || imp.idImpuesto === 30)
            )
            
            // Solo autocompletar si tiene IVA (para Factura A solo aceptamos RI)
            if (tieneIVA) {
              this.form.condicionIVA = 'RESPONSABLE_INSCRIPTO'
            }
          }

          // Mostrar estado del contribuyente
          const estadoFinal = dg.estadoClave || persona.estadoClave
          if (estadoFinal) {
            if (estadoFinal === 'ACTIVO') {
              this.snackbarColor = 'success'
              this.snackbarMessage = `Cliente encontrado en AFIP - Estado: ${estadoFinal}`
            } else {
              this.snackbarColor = 'warning'
              this.snackbarMessage = `Cliente encontrado en AFIP - Estado: ${estadoFinal}`
            }
          } else {
            this.snackbarColor = 'success'
            this.snackbarMessage = 'Datos obtenidos de AFIP correctamente'
          }
        } else {
          this.snackbarColor = 'error'
          this.snackbarMessage = 'No se encontraron datos del contribuyente en AFIP'
        }
      } catch (error) {
        console.error('Error consultando ARCA:', error)
        this.snackbarColor = 'error'
        
        // Mostrar mensaje amigable según el tipo de error
        if (error.message && error.message.includes('Network')) {
          this.snackbarMessage = 'Error de conexión. Verifique su conexión a internet e intente nuevamente.'
        } else if (error.message && error.message.includes('timeout')) {
          this.snackbarMessage = 'La consulta a AFIP tardó demasiado. Por favor intente nuevamente.'
        } else {
          this.snackbarMessage = 'Error al consultar datos de AFIP. Por favor intente nuevamente.'
        }
      } finally {
        this.consultandoArca = false
      }
    },
    
    crear() {
      if (!this.$refs.form.validate()) return
      this.docVentaReminderOpen = true
    },

    async confirmarDocumentosVenta() {
      this.docVentaReminderOpen = false
      await this.performCrear()
    },

    async performCrear() {
      this.creando = true
      this.error = null

      const nd = this.form.numeroDocumento && String(this.form.numeroDocumento).trim()
        ? String(this.form.numeroDocumento).trim()
        : null

      try {
        const response = await ClienteProvider.createCliente({
          tipoDocumento: this.form.tipoDocumento,
          numeroDocumento: nd,
          razonSocial: this.form.razonSocial,
          condicionIVA: this.form.condicionIVA,
          email: this.form.email || null,
          activo: true
        })
        
        const clienteCreado = response.data.clienteCreate
        
        // Emitir evento con el cliente creado para que se seleccione automáticamente
        this.$emit('cliente-creado', clienteCreado)
        
        // Limpiar y cerrar
        this.limpiarForm()
        this.visible = false
        
      } catch (err) {
        this.error = err.message
      } finally {
        this.creando = false
      }
    },
    
    cancelar() {
      this.limpiarForm()
      this.visible = false
    },
    
    limpiarForm() {
      this.form = {
        tipoDocumento: this.tipoFactura === 'FACTURA_B' ? 'DNI' : 'CUIT',
        numeroDocumento: '',
        razonSocial: '',
        condicionIVA: this.tipoFactura === 'FACTURA_B' ? 'CONSUMIDOR_FINAL' : 'RESPONSABLE_INSCRIPTO',
        email: ''
      }
      this.error = null
      if (this.$refs.form) {
        this.$refs.form.resetValidation()
      }
    }
  }
}
</script>
